import classNames from "classnames";
import React, { useLayoutEffect, useRef, useState } from "react";
import "./kuika-swap.scss";

export type KuikaSwapKey = string | number;

/** `roll` — the content rolls vertically through a blur · `blur` — it shrinks and blurs in place. */
export type KuikaSwapVariant = "roll" | "blur";

/** `up` — new content rises from below · `down` — it drops from above (e.g. a counter going down). */
export type KuikaSwapDirection = "up" | "down";

/**
 * Where the leaving content sits while it fades out.
 *
 * - `inline` (default) — it shares the incoming content's grid cell, so the slot is as wide and as
 *   tall as the larger of the two until the exit ends. Right whenever the slot's size is set by
 *   something other than its content: a tab pane, a fixed cell, a digit in an OTP box.
 * - `overlay` — it is taken out of flow and centred over the cell, so the slot is the new content's
 *   size from the first frame. Pick it for an inline label inside a control that sizes itself to its
 *   content — a button's status label, a copy button, a badge — where an `inline` leaver makes the
 *   owner swell to both labels and snap back when the exit finishes (worse still in a background tab,
 *   where the exit is paused and the owner stays swollen).
 */
export type KuikaSwapLeaving = "inline" | "overlay";

export interface IKuikaSwapProps {
  /** Identity of the content. Changing it swaps; re-rendering with the same key updates in place. */
  contentKey: KuikaSwapKey;
  children: React.ReactNode;
  variant?: KuikaSwapVariant;
  direction?: KuikaSwapDirection;
  /** `span` (default) fits inline content such as a button label; `div` for block content such as a tab pane. */
  as?: "span" | "div";
  /** Where the outgoing copy sits while it leaves — see `KuikaSwapLeaving`. Default `inline`. */
  leaving?: KuikaSwapLeaving;
  className?: string;
}

interface ISwapEntry {
  key: string;
  node: React.ReactNode;
}

/**
 * A slot whose content changes with motion instead of cutting: when `contentKey` changes, the old
 * content leaves (`kuika-swap-out`, a shortened exit) while the new one arrives (`kuika-swap-in`),
 * both in the same grid cell. Use it for a button's status label, a copy icon turning into a check,
 * a status badge, a number, a tab's content.
 *
 * - The first render never animates; only swaps do.
 * - The outgoing copy is `aria-hidden` and ignores the pointer, so assistive tech reads the text once.
 *   Announcing the change (`aria-live`) is the owner's call, on its own element.
 * - One outgoing copy at most: a swap during a swap drops the older leaver at once.
 * - The leaver is removed on its own `animationend`. Inside a `display: none` subtree the exit does
 *   not run; it plays (and ends) once the subtree is shown again, so no copy is left behind.
 * - `leaving` decides whether the leaver holds the slot open (`inline`) or is laid over it
 *   (`overlay`) — see `KuikaSwapLeaving` for which to pick.
 * - Reduced motion needs nothing here: the keyframes scale their travel, scale and blur by the
 *   `--motion-*` multipliers, so under it the swap is a plain crossfade.
 */
const KuikaSwap = ({
  contentKey,
  children,
  variant = "roll",
  direction = "up",
  as = "span",
  leaving = "inline",
  className
}: IKuikaSwapProps): JSX.Element => {
  const currentKey: string = String(contentKey);
  const committedRef: React.MutableRefObject<ISwapEntry> = useRef<ISwapEntry>({ key: currentKey, node: children });
  const [outgoing, setOutgoing] = useState<ISwapEntry | null>(null);
  const [hasSwapped, setHasSwapped] = useState<boolean>(false);

  // A layout effect, so the leaver is in place before the browser paints the new content alone.
  useLayoutEffect((): void => {
    const committed: ISwapEntry = committedRef.current;

    if (committed.key !== currentKey) {
      setOutgoing(committed);
      setHasSwapped(true);
    }

    committedRef.current = { key: currentKey, node: children };
  }, [currentKey, children]);

  const handleOutgoingDone = (event: React.AnimationEvent<HTMLElement>): void => {
    // Animations inside the content bubble here too; only the item's own exit ends it.
    if (event.target !== event.currentTarget) {
      return;
    }

    setOutgoing(null);
  };

  // Swapping back to the key that is still leaving: until the effect above hands the slot's current
  // content to `outgoing`, the stale leaver would share the incoming item's key.
  const leaver: ISwapEntry | null = outgoing !== null && outgoing.key !== currentKey ? outgoing : null;
  const Element: "span" | "div" = as;

  return (
    <Element
      className={classNames(
        "kuika-swap",
        `kuika-swap--${variant}`,
        `kuika-swap--${direction}`,
        { "kuika-swap--block": as === "div", "kuika-swap--overlay": leaving === "overlay" },
        className
      )}
    >
      {leaver !== null && (
        <Element
          key={leaver.key}
          className="kuika-swap__item kuika-swap__item--out"
          aria-hidden="true"
          onAnimationEnd={handleOutgoingDone}
        >
          {leaver.node}
        </Element>
      )}
      <Element key={currentKey} className={classNames("kuika-swap__item", { "kuika-swap__item--in": hasSwapped })}>
        {children}
      </Element>
    </Element>
  );
};

export default KuikaSwap;
